import { faCircleDot } from "@fortawesome/pro-solid-svg-icons";
import { faCircle } from "@fortawesome/pro-thin-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import useControlledState from "../../../hooks/useControlledState";

// See: https://mui.com/material-ui/react-checkbox/
// See: https://ant.design/components/checkbox

export const RadioColor = {
  primary500: "primary500",
} as const;

export const RadioSize = {
  small: "small",
  medium: "medium",
  large: "large",
} as const;

export const RadioState = {
  disabled: "disabled",
  normal: "normal",
} as const;

const RadioColorConfig = {
  [RadioColor.primary500]: {
    checked: "text-vpBlue-500",
    regular: "hover:text-vpBlue-500",
  },
} as const;

const RadioSizeConfig = {
  [RadioSize.small]: "text-xl",
  [RadioSize.medium]: "text-2xl",
  [RadioSize.large]: "text-3xl",
} as const;

const RadioStateConfig = {
  [RadioState.disabled]: "text-vpGray-300 hover:text-vpGray-300",
  [RadioState.normal]: "text-vpGray-500",
} as const;

export type RadioRootNode = HTMLDivElement;

export interface RadioProps extends React.HTMLAttributes<RadioRootNode> {
  color?: keyof typeof RadioColor;
  defaultChecked?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  label?: React.ReactNode;
  name?: string;
  onChange?: React.HTMLAttributes<HTMLInputElement>["onChange"];
  checked?: boolean;
  checkedIcon?: React.ReactNode;
  size?: keyof typeof RadioSize;
  slots?: {
    root?: {
      css?: string;
    };
    iconBox?: {
      css?: string;
    };
    input?: {
      css?: string;
    };
    label?: {
      css?: string;
    };
  };
  value?: string;
}

const Radio = forwardRef<RadioRootNode, RadioProps>(
  (
    {
      checked: checkedProp,
      checkedIcon,
      className,
      color = RadioColor.primary500,
      defaultChecked = false,
      disabled,
      icon,
      id,
      label,
      name,
      onChange,
      size = RadioSize.medium,
      slots,
      value,
      ...props
    },
    ref
  ) => {
    const [checked, setChecked] = useControlledState({
      uncontrolledValue: defaultChecked,
      controlledValue: checkedProp,
    });

    return (
      <div className={twMerge(classNames("flex items-center gap-2", className, slots?.root?.css))} {...props} ref={ref}>
        <div
          className={twMerge(
            classNames(
              "relative",
              RadioStateConfig.normal,
              RadioSizeConfig[size],
              checked ? RadioColorConfig[color].checked : RadioColorConfig[color].regular,
              {
                [RadioStateConfig.disabled]: disabled,
              },
              slots?.iconBox?.css
            )
          )}
        >
          <input
            className={twMerge(
              classNames(
                "absolute left-0 top-0 z-10 m-0 h-full w-full border-0 p-0 opacity-0",
                { "cursor-pointer": !disabled },
                slots?.input?.css
              )
            )}
            disabled={disabled}
            id={id}
            name={name}
            onChange={(event) => {
              setChecked(!checked);

              onChange?.(event);
            }}
            type="radio"
            value={value}
          />
          {checked
            ? checkedIcon ?? <FontAwesomeIcon icon={faCircleDot} />
            : icon ?? <FontAwesomeIcon icon={faCircle} />}
        </div>
        {label && (
          <label
            className={twMerge(
              classNames(disabled ? "text-vpGray-400 hover:text-vpGray-400" : "cursor-pointer", slots?.label?.css)
            )}
            htmlFor={id}
          >
            {label}
          </label>
        )}
      </div>
    );
  }
);

Radio.displayName = Radio.name;

export default Radio;
